import React, { useState, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

// Lightweight OverlayTrigger + Tooltip + Popover (react-bootstrap-compatible surface).
// Hover/focus (or click) reveals a positioned overlay.
//
// The overlay is rendered in a portal on <body> with position:fixed so it can never be
// clipped by an ancestor's overflow:hidden (e.g. the home dashboard's grouped cards) or
// covered by a neighbouring component. Default placement is "right"; if the overlay would
// run off the viewport it flips to the opposite side and is clamped back on-screen.
type Placement = 'top' | 'bottom' | 'left' | 'right';

const GAP = 8; // px between the trigger and the overlay

export const Tooltip: React.FC<React.HTMLAttributes<HTMLDivElement> & { id?: string }> = ({ className = '', children, ...rest }) => (
  <div className={`tw-max-w-xs tw-rounded tw-bg-content tw-px-2 tw-py-1 tw-text-xs tw-text-white tw-shadow-lg ${className}`} {...rest}>
    {children}
  </div>
);

type PopoverPartProps = React.HTMLAttributes<HTMLElement> & { as?: React.ElementType };
interface PopoverType extends React.FC<React.HTMLAttributes<HTMLDivElement> & { id?: string }> {
  Header: React.FC<PopoverPartProps>;
  Body: React.FC<PopoverPartProps>;
  Title: React.FC<PopoverPartProps>;
}
export const Popover = (({ className = '', children, ...rest }) => (
  <div className={`tw-min-w-[12rem] tw-rounded tw-border tw-border-line tw-bg-surface tw-shadow-lg ${className}`} {...rest}>
    {children}
  </div>
)) as PopoverType;
Popover.Header = ({ as, className = '', ...rest }) => {
  const Comp: React.ElementType = as || 'div';
  return <Comp className={`tw-border-b tw-border-line tw-bg-surface-alt tw-px-3 tw-py-2 tw-font-medium ${className}`} {...rest} />;
};
Popover.Title = Popover.Header;
Popover.Body = ({ as, className = '', ...rest }) => {
  const Comp: React.ElementType = as || 'div';
  return <Comp className={`tw-px-3 tw-py-2 ${className}`} {...rest} />;
};

/** Fixed-viewport coordinates for the overlay given the trigger rect, overlay size and placement. */
function computePosition(
  rect: DOMRect,
  size: { w: number; h: number },
  placement: Placement,
): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const place = (p: Placement) => {
    switch (p) {
      case 'right':
        return { top: rect.top + rect.height / 2 - size.h / 2, left: rect.right + GAP };
      case 'left':
        return { top: rect.top + rect.height / 2 - size.h / 2, left: rect.left - GAP - size.w };
      case 'top':
        return { top: rect.top - GAP - size.h, left: rect.left + rect.width / 2 - size.w / 2 };
      case 'bottom':
      default:
        return { top: rect.bottom + GAP, left: rect.left + rect.width / 2 - size.w / 2 };
    }
  };

  let pos = place(placement);
  // Flip to the opposite side if the preferred placement runs off-screen.
  if (placement === 'right' && pos.left + size.w > vw - 4) pos = place('left');
  else if (placement === 'left' && pos.left < 4) pos = place('right');
  else if (placement === 'top' && pos.top < 4) pos = place('bottom');
  else if (placement === 'bottom' && pos.top + size.h > vh - 4) pos = place('top');

  // Clamp within the viewport so it's never partially hidden.
  pos.left = Math.max(4, Math.min(pos.left, vw - size.w - 4));
  pos.top = Math.max(4, Math.min(pos.top, vh - size.h - 4));
  return pos;
}

export interface OverlayTriggerProps {
  placement?: Placement;
  overlay: React.ReactElement;
  trigger?: 'hover' | 'click' | 'focus' | Array<'hover' | 'click' | 'focus'> | null;
  show?: boolean;
  defaultShow?: boolean;
  children: React.ReactElement;
}
export const OverlayTrigger: React.FC<OverlayTriggerProps> = ({
  placement = 'right',
  overlay,
  trigger = ['hover', 'focus'],
  show,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const overlayRef = useRef<HTMLSpanElement>(null);
  const shown = show !== undefined ? show : open;
  const listed = (Array.isArray(trigger) ? trigger : trigger != null ? [trigger] : []).filter(
    (item): item is 'hover' | 'click' | 'focus' => item === 'hover' || item === 'click' || item === 'focus',
  );
  const triggers = listed.length > 0 ? listed : ['hover', 'focus'];

  const reposition = useCallback(() => {
    if (!triggerRef.current || !overlayRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const o = overlayRef.current.getBoundingClientRect();
    setCoords(computePosition(rect, { w: o.width, h: o.height }, placement));
  }, [placement]);

  // Measure and place once the overlay is in the DOM; keep it anchored on scroll/resize.
  useLayoutEffect(() => {
    if (!shown) {
      setCoords(null);
      return;
    }
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [shown, reposition]);

  const handlers: React.HTMLAttributes<HTMLSpanElement> = {};
  if (triggers.includes('hover')) {
    handlers.onMouseEnter = () => setOpen(true);
    handlers.onMouseLeave = () => setOpen(false);
  }
  if (triggers.includes('focus')) {
    handlers.onFocus = () => setOpen(true);
    handlers.onBlur = () => setOpen(false);
  }
  if (triggers.includes('click')) {
    handlers.onClick = () => setOpen((o) => !o);
  }

  return (
    <>
      <span ref={triggerRef} className="tw-inline-flex" {...handlers}>
        {children}
      </span>
      {shown &&
        createPortal(
          <span
            ref={overlayRef}
            role="tooltip"
            className="tw-z-[1080]"
            style={{
              position: 'fixed',
              top: coords ? coords.top : -9999,
              left: coords ? coords.left : -9999,
              // Hide until measured so it doesn't flash at the wrong spot.
              visibility: coords ? 'visible' : 'hidden',
            }}
          >
            {overlay}
          </span>,
          document.body,
        )}
    </>
  );
};

export default OverlayTrigger;
